/**
 * CustomerDashboard — `/dashboard/customer` overview/index page.
 *
 * Phase 2 of the App Shell Redesign trims this from a 742-line all-in-
 * one dashboard to the overview surface: greeting + credit card + the
 * profile editor form. The bookings rail moved to
 * `CustomerBookingsPage` (`/dashboard/customer/bookings`), the dogs
 * section moved to `CustomerDogsPage` (`/dashboard/customer/dogs`),
 * and the credit-history drawer was inlined into `CustomerBillingPage`
 * (`/dashboard/customer/billing`).
 *
 * This page now renders INSIDE `<CustomerShell>` via `<Outlet />` —
 * `<SharedNav>` + `<SharedFooter>` are no longer rendered here (the
 * shell owns its own top region per Decision #6 + drops the footer
 * per Decision #18).
 *
 * All prior data-testids on retained content (`customer-dashboard`,
 * `customer-credit-card`, `customer-credit-balance`,
 * `customer-profile-card`, `customer-profile-form`,
 * `customer-save-button`, address / phone / yard-size / dog-count /
 * notes inputs, secondary-contact block, etc.) are preserved verbatim.
 * Tests covering booking / dog flows now run against the dedicated
 * sub-pages — same testids, new route.
 */
import React, { useEffect, useState } from "react";
import TimezoneBanner from "@/components/TimezoneBanner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImpersonationConfirmGate, {
  useImpersonationGate,
} from "@/components/ImpersonationConfirmGate";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";
import RateMyPluckBanner from "@/components/customer/RateMyPluckBanner";
import PooBucksBalanceChip from "@/components/customer/PooBucksBalanceChip";
import WelcomeToPackPopup from "@/components/WelcomeToPackPopup";
import { Loader2, Plus, X } from "lucide-react";

const YARD_SIZES = [
  { v: "small", label: "Small (< ¼ acre)" },
  { v: "medium", label: "Medium (¼–½ acre)" },
  { v: "large", label: "Large (½–1 acre)" },
  { v: "xlarge", label: "Extra large (> 1 acre)" },
];

const PHONE_RE = /^\d{3}\.\d{3}\.\d{4}$/;

export default function CustomerDashboard() {
  const { user, setProfile } = useAuth();
  const { creditLabel } = useConfig();
  const [profile, setLocalProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const [form, setForm] = useState({
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    address_verified: false,
    address_provider: null,
    address_zip4: null,
    address_lat: null,
    address_lng: null,
    yard_size: "medium",
    dog_count: 1,
    notes: "",
    primary_phone: "",
    secondary_contact: null,
  });

  const refetch = async () => {
    const { data } = await api.get("/profiles/customer/me");
    setLocalProfile(data);
    setProfile(data);
    setForm((s) => ({
      ...s,
      address_line1: data.address_line1 || "",
      address_line2: data.address_line2 || "",
      city: data.city || "",
      state: data.state || "",
      zip: data.zip || "",
      address_verified: !!data.address_verified,
      address_provider: data.address_provider || null,
      address_zip4: data.address_zip4 || null,
      address_lat: data.address_lat || null,
      address_lng: data.address_lng || null,
      yard_size: data.yard_size || "medium",
      dog_count: data.dog_count ?? 1,
      notes: data.notes || "",
      primary_phone: data.primary_phone || "",
      secondary_contact: data.secondary_contact || null,
    }));
  };

  useEffect(() => {
    (async () => {
      try {
        await refetch();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoaded(true);
      }
    })();
    // Mount-only profile fetch. `refetch` is a local async function
    // whose identity churns each render; adding it to deps would
    // infinite-loop the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const { requestAction, gateProps } = useImpersonationGate();

  const phoneInvalid = form.primary_phone && !PHONE_RE.test(form.primary_phone);
  const secInvalid =
    form.secondary_contact &&
    !!form.secondary_contact.phone &&
    !PHONE_RE.test(form.secondary_contact.phone);

  const reallySave = async () => {
    // Phase 4a Step 4 — Mandatory USPS-verify gate. If the customer
    // has typed an address (line1 non-empty) but it isn't verified,
    // block the save with a friendly toast. Empty-profile saves
    // (no address_line1 at all) still go through — the customer
    // just hasn't filled it in yet. Editing a verified address
    // resets `address_verified` via the AddressAutocomplete
    // component + the backend PATCH invariant, so the gate re-fires
    // on the next Save until the user completes verification.
    if ((form.address_line1 || "").trim() && !form.address_verified) {
      toast.error("Please verify your address before saving.");
      return;
    }
    if (form.primary_phone && !PHONE_RE.test(form.primary_phone)) {
      toast.error("Primary phone must be in 402.555.0142 format.");
      return;
    }
    if (form.secondary_contact && !PHONE_RE.test(form.secondary_contact.phone || "")) {
      toast.error("Secondary contact phone must be in 402.555.0142 format.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        state: form.state,
        zip: form.zip,
        yard_size: form.yard_size,
        dog_count: Number(form.dog_count) || 0,
        notes: form.notes,
        primary_phone: form.primary_phone || "",
        secondary_contact: form.secondary_contact,
      };
      if (form.address_verified) {
        payload.address_verified = true;
        payload.address_provider = form.address_provider;
        payload.address_zip4 = form.address_zip4;
        payload.address_lat = form.address_lat;
        payload.address_lng = form.address_lng;
      }
      const { data } = await api.patch("/profiles/customer", payload);
      setLocalProfile(data);
      setProfile(data);
      toast.success("Profile saved!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (e) => {
    e?.preventDefault();
    requestAction("save profile changes", reallySave);
  };

  const completion = (() => {
    const fields = [
      "address_line1",
      "city",
      "state",
      "zip",
      "yard_size",
      "primary_phone",
    ];
    const filled = fields.filter((f) => `${form[f] ?? ""}`.trim() !== "").length;
    return Math.round((filled / fields.length) * 100);
  })();

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <header className="mb-8" data-testid="customer-dashboard">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Customer dashboard
        </span>
        <h1 className="font-heading text-4xl font-bold text-pp-text sm:text-5xl">
          Hi {user?.name?.split(" ")[0] || "there"} 👋
        </h1>
        <p className="mt-2 text-muted-foreground">
          Keep your profile up to date so your PooPartner can give you accurate quotes.
        </p>
      </header>

      {/* Item #6 — Rate My Pluck banner. Renders nothing when the
          customer has zero completed-unreviewed bookings (intentional
          empty state). Auto-opens the most-recent unreviewed booking
          one-time per device via localStorage `pp_review_autoopen_shown`. */}
      <RateMyPluckBanner />

      {/* Track B — legacy-cohort timezone banner. Renders only when
          the customer's profile.timezone is unset; dismissible for
          the current session via sessionStorage. Consumes the
          existing `profile` state — no extra network fetch. */}
      <TimezoneBanner
        profileTimezone={profile?.timezone}
        settingsHref="/dashboard/customer/settings"
      />

      {/* PooBucks compact chip. Read-only balance display; ledger
          history lives at `/dashboard/customer/billing`. Reuses the
          denormalized `profile.credit_balance` field (same source
          `CustomerBillingPage` reads at line 35) \u2014 no extra fetch.
          Item M2: replaces the earlier `customer-credit-card` +
          "Applied automatically to future bookings (coming soon)"
          card, which hinted at a redemption UX not yet built. The
          compact chip is neutral by design: positive balances in
          brand-orange, zero balances rendered as `$0.00` in the
          muted color with zero upsell copy. */}
      <div className="mb-8" data-testid="customer-poobucks-chip-slot">
        <PooBucksBalanceChip />
      </div>

      <Card
        className="rounded-3xl border-border bg-pp-surface p-8 shadow-none"
        data-testid="customer-profile-card"
      >
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-pp-text">
              Your home &amp; contacts
            </h2>
            <p className="text-sm text-muted-foreground">
              Profile completion:{" "}
              <span className="font-semibold text-pp-text" data-testid="profile-completion-pct">
                {completion}%
              </span>
            </p>
          </div>
          <div className="h-2 w-32 overflow-hidden rounded-full bg-secondary">
            <div className="h-full bg-brand-orange transition-all" style={{ width: `${completion}%` }} />
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-5" data-testid="customer-profile-form">
          {!loaded && (
            <p className="text-sm text-muted-foreground" data-testid="customer-profile-loading">
              Loading your profile…
            </p>
          )}
          <fieldset disabled={!loaded} className="space-y-5 disabled:opacity-60">
            <AddressAutocomplete
              value={{
                address_line1: form.address_line1,
                address_line2: form.address_line2,
                city: form.city,
                state: form.state,
                zip: form.zip,
                address_verified: form.address_verified,
                address_provider: form.address_provider,
                address_zip4: form.address_zip4,
                address_lat: form.address_lat,
                address_lng: form.address_lng,
              }}
              onChange={(next) => setForm((s) => ({ ...s, ...next }))}
            />

            {!form.address_verified && (
              <div className="grid gap-4 sm:grid-cols-3" data-testid="address-manual-fields">
                <div>
                  <Label className="text-sm font-semibold text-pp-text">City</Label>
                  <Input
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    className="mt-2 h-12 rounded-full px-5"
                    data-testid="customer-city-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-pp-text">State</Label>
                  <Input
                    value={form.state}
                    onChange={(e) => update("state", e.target.value)}
                    className="mt-2 h-12 rounded-full px-5"
                    data-testid="customer-state-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-pp-text">ZIP</Label>
                  <Input
                    value={form.zip}
                    onChange={(e) => update("zip", e.target.value)}
                    className="mt-2 h-12 rounded-full px-5"
                    data-testid="customer-zip-input"
                  />
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-sm font-semibold text-pp-text">Yard size</Label>
                <Select value={form.yard_size} onValueChange={(v) => update("yard_size", v)}>
                  <SelectTrigger
                    className="mt-2 h-12 rounded-full px-5"
                    data-testid="customer-yard-size-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YARD_SIZES.map((y) => (
                      <SelectItem key={y.v} value={y.v}>
                        {y.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold text-pp-text">Number of dogs</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.dog_count}
                  onChange={(e) => update("dog_count", e.target.value)}
                  className="mt-2 h-12 rounded-full px-5"
                  data-testid="customer-dog-count-input"
                />
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-muted p-5">
              <p className="mb-4 font-heading text-lg font-semibold text-pp-text">Contacts</p>
              <div>
                <Label className="text-sm font-semibold text-pp-text">Primary phone</Label>
                <PhoneInput
                  value={form.primary_phone}
                  onChange={(v) => update("primary_phone", v)}
                  invalid={!!phoneInvalid}
                  className="mt-2"
                  data-testid="customer-primary-phone-input"
                />
                {phoneInvalid && (
                  <p className="mt-1 text-xs text-destructive" data-testid="primary-phone-error">
                    Use the format 402.555.0142.
                  </p>
                )}
              </div>

              {!form.secondary_contact ? (
                <button
                  type="button"
                  onClick={() =>
                    update("secondary_contact", { name: "", phone: "", notify: false })
                  }
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-orange hover:underline"
                  data-testid="add-secondary-contact-link"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add a secondary contact
                </button>
              ) : (
                <div className="mt-5 space-y-3 rounded-xl bg-pp-surface p-4" data-testid="secondary-contact-block">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-pp-text">Secondary contact</p>
                    <button
                      type="button"
                      onClick={() => update("secondary_contact", null)}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                      data-testid="remove-secondary-contact-link"
                    >
                      <X className="h-3 w-3" />
                      Remove
                    </button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs font-semibold text-pp-text">Name</Label>
                      <Input
                        value={form.secondary_contact.name || ""}
                        onChange={(e) =>
                          update("secondary_contact", {
                            ...form.secondary_contact,
                            name: e.target.value,
                          })
                        }
                        className="mt-1.5 h-10 rounded-full px-4"
                        data-testid="secondary-contact-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-pp-text">Phone</Label>
                      <PhoneInput
                        value={form.secondary_contact.phone || ""}
                        onChange={(v) =>
                          update("secondary_contact", { ...form.secondary_contact, phone: v })
                        }
                        invalid={!!secInvalid}
                        className="mt-1.5 h-10"
                        data-testid="secondary-contact-phone-input"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-pp-text">
                    <Checkbox
                      checked={!!form.secondary_contact.notify}
                      onCheckedChange={(c) =>
                        update("secondary_contact", {
                          ...form.secondary_contact,
                          notify: !!c,
                        })
                      }
                      data-testid="secondary-contact-notify-checkbox"
                    />
                    Send notifications to this contact too
                  </label>
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-semibold text-pp-text">
                Notes for your provider
              </Label>
              <Textarea
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                placeholder="Gate code, friendly pups, etc."
                className="mt-2 rounded-2xl px-5 py-3"
                data-testid="customer-notes-input"
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={saving || !loaded}
                className="h-12 rounded-full bg-pp-orange px-8 text-pp-cta-text hover:bg-pp-orange-hover"
                data-testid="customer-save-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save profile"
                )}
              </Button>
            </div>
          </fieldset>
        </form>
      </Card>
      <ImpersonationConfirmGate {...gateProps} />
      {/* One-time post-signup welcome popup — server-side flag gated,
          hidden during admin impersonation. See WelcomeToPackPopup. */}
      <WelcomeToPackPopup role="customer" />
    </div>
  );
}
